// =============================================================================
// HUD（DOMオーバーレイ）。3Dシーンには触れず、画面上にUIを重ねる。
// 体力/空腹/渇き/スタミナ、ホットバー、採取プロンプト、クラフト、資源マーカー、死亡。
// =============================================================================
import { projectToScreen } from './projection';
import { ITEMS, RECIPES, inventory, count, canCraft } from './crafting';
import { featList } from './milestones';
import { WORLD_PER_M } from './world';
import type { Stats, ResourceNode } from './types';

let el: any = {};

export function buildHud(handlers: { onCraft?: (id: string) => void; onRestart?: () => void } = {}): any {
  const style = document.createElement('style');
  style.textContent = `
  #hud-root{position:fixed;inset:0;z-index:9000;pointer-events:none;font-family:Montserrat,system-ui,-apple-system,sans-serif;color:#eaf2f8;user-select:none;}
  #hud-root *{box-sizing:border-box;}
  #crosshair{position:absolute;left:50%;top:50%;width:6px;height:6px;margin:-3px 0 0 -3px;border-radius:50%;background:rgba(255,255,255,.85);box-shadow:0 0 3px rgba(0,0,0,.6);}
  /* ノッチ/ホームバー退避: env()はviewport-fit=cover時のみ非0(非対応環境は0px=無変化)。上端は連動要素が多くブラウザ内では常に0のため対象外 */
  #stats{position:absolute;left:calc(16px + env(safe-area-inset-left, 0px));bottom:calc(16px + env(safe-area-inset-bottom, 0px));width:230px;display:flex;flex-direction:column;gap:6px;}
  .bar{height:14px;border-radius:7px;background:rgba(0,0,0,.45);overflow:hidden;position:relative;box-shadow:0 0 0 1px rgba(255,255,255,.12) inset;}
  .bar>i{display:block;height:100%;width:50%;transition:width .15s linear;}
  .bar>span{position:absolute;left:8px;top:0;line-height:14px;font-size:10px;letter-spacing:1px;text-shadow:0 1px 2px #000;}
  #b-health>i{background:linear-gradient(90deg,#ff5a5a,#ff8e8e);}
  #b-hunger>i{background:linear-gradient(90deg,#e0953b,#ffce7a);}
  #b-thirst>i{background:linear-gradient(90deg,#4aa3ff,#8fd0ff);}
  #b-stamina>i{background:linear-gradient(90deg,#6bd06b,#b6f0a6);}
  #hotbar{position:absolute;left:50%;bottom:calc(16px + env(safe-area-inset-bottom, 0px));transform:translateX(-50%);display:flex;flex-wrap:wrap;justify-content:center;gap:6px;max-width:88vw;pointer-events:auto;}
  .slot{cursor:pointer;transition:transform .08s;}
  .slot:active{transform:scale(.94);}
  .slot.eq{box-shadow:0 0 0 2px #4facfe inset, 0 0 10px rgba(79,172,254,.55);background:rgba(20,44,66,.8);}
  .slot.flash{animation:slotflash .7s ease-out;}
  @keyframes slotflash{0%{box-shadow:0 0 0 2px #ffd76e inset, 0 0 16px rgba(255,215,110,.9);transform:scale(1.12);}100%{}}
  .slot em{position:absolute;left:3px;bottom:1px;font-size:8px;font-style:normal;opacity:.55;}
  .slot{width:48px;height:48px;border-radius:8px;background:rgba(8,20,32,.55);box-shadow:0 0 0 1px rgba(255,255,255,.15) inset;display:flex;flex-direction:column;align-items:center;justify-content:center;font-size:20px;position:relative;}
  .slot b{position:absolute;right:3px;bottom:1px;font-size:11px;font-weight:600;text-shadow:0 1px 2px #000;}
  .slot small{font-size:8px;opacity:.7;position:absolute;left:0;top:2px;width:100%;text-align:center;}
  #prompt{position:absolute;left:50%;top:58%;transform:translateX(-50%);background:rgba(0,0,0,.55);padding:7px 14px;border-radius:8px;font-size:14px;opacity:0;transition:opacity .1s;white-space:nowrap;}
  #prompt.on{opacity:1;}
  #prompt kbd{background:#eaf2f8;color:#111;border-radius:4px;padding:1px 6px;font-weight:700;margin-right:6px;}
  #mode{position:absolute;left:calc(16px + env(safe-area-inset-left, 0px));top:12px;font-size:11px;letter-spacing:1px;background:rgba(0,0,0,.4);padding:4px 8px;border-radius:6px;opacity:.85;}
  #markers{position:absolute;inset:0;}
  .marker{position:absolute;transform:translate(-50%,-50%);text-align:center;font-size:22px;filter:drop-shadow(0 1px 2px #000);will-change:transform,left,top;}
  .marker em{display:block;font-style:normal;font-size:10px;opacity:.85;margin-top:-2px;text-shadow:0 1px 2px #000;}
  #craft{position:absolute;right:calc(16px + env(safe-area-inset-right, 0px));top:50%;transform:translateY(-50%);width:280px;max-height:80vh;overflow:auto;background:rgba(6,16,26,.92);border-radius:12px;padding:14px;box-shadow:0 8px 30px rgba(0,0,0,.5);display:none;pointer-events:auto;}
  #craft.on{display:block;}
  /* 画面外レシピ(銛・調理など)の気付き: 下端に「▼」フェードを重ね、最下段まで見たら消す。
     macOSのoverlayスクロールバーでは ::-webkit-scrollbar のサムが描画されないため、バー表示には頼らない */
  #craft::-webkit-scrollbar{width:6px;}
  #craft::-webkit-scrollbar-thumb{background:rgba(255,255,255,.22);border-radius:3px;}
  #craft::after{content:'▼';position:sticky;display:block;bottom:-14px;height:24px;margin-top:-24px;background:linear-gradient(transparent, rgba(6,16,26,.98) 72%);color:rgba(255,255,255,.5);font-size:9px;text-align:center;line-height:30px;pointer-events:none;opacity:0;transition:opacity .15s;}
  #craft.more::after{opacity:1;}
  #craft h3{font-size:13px;letter-spacing:2px;text-transform:uppercase;opacity:.7;margin-bottom:10px;}
  .recipe{display:flex;align-items:center;gap:10px;padding:8px;border-radius:8px;cursor:pointer;margin-bottom:4px;background:rgba(255,255,255,.04);}
  .recipe:hover{background:rgba(255,255,255,.10);}
  .recipe.no{opacity:.4;cursor:not-allowed;}
  .recipe .ic{font-size:22px;}
  .recipe .nm{flex:1;}
  .recipe .nm b{font-size:13px;}
  .recipe .nm small{display:block;font-size:10px;opacity:.7;}
  .recipe .nm small.use{font-size:9px;opacity:.5;} /* 用途の一行説明(素材行より控えめ) */
  #hint{position:absolute;right:calc(16px + env(safe-area-inset-right, 0px));top:12px;font-size:11px;opacity:.6;background:rgba(0,0,0,.35);padding:4px 8px;border-radius:6px;text-align:right;line-height:1.5;}
  #hud-root.touch #hint{display:none;} /* タッチ端末にキーボード説明は不要(横持ちは幅@mediaを抜けるためクラスで) */
  #hud-root.touch #mode .co{display:none;} /* デバッグ座標も同様(横持ちで出ていた) */
  #goal{position:absolute;left:calc(16px + env(safe-area-inset-left, 0px));top:40px;font-size:11px;letter-spacing:.5px;background:rgba(0,0,0,.4);padding:4px 8px;border-radius:6px;opacity:.9;max-width:calc(50vw - 154px);line-height:1.5;} /* コンパス帯(中央260px)の左端手前で折り返す。46vwだと横持ち/長文で帯に潜り込んだ */
  #win{position:absolute;inset:0;background:linear-gradient(rgba(4,24,40,.30), rgba(4,24,40,.66));display:none;align-items:center;justify-content:center;flex-direction:column;pointer-events:auto;}
  #win.on{display:flex;animation:screenfade 1.2s ease-out;}
  #win .big{font-size:32px;letter-spacing:6px;}
  #win .rank{font-size:46px;letter-spacing:4px;margin-top:16px;text-shadow:0 4px 18px rgba(255,215,110,.45);animation:screenfade 1.6s ease-out;}
  #win .sub{font-size:15px;letter-spacing:2px;margin-top:14px;opacity:.9;text-align:center;max-width:86vw;line-height:1.7;}
  #win .restart{margin-top:26px;cursor:pointer;background:#eaf2f8;color:#111;border:none;border-radius:8px;padding:10px 24px;font-size:15px;font-weight:700;letter-spacing:2px;}
  #win .restart:hover{background:#fff;}
  #win .cf{position:absolute;top:-20px;width:8px;height:12px;border-radius:2px;opacity:0;animation:cffall linear infinite;}
  @keyframes cffall{0%{opacity:0;transform:translateY(0) rotate(0deg);}8%{opacity:1;}100%{opacity:.85;transform:translateY(105vh) rotate(720deg);}}
  #death{position:absolute;inset:0;background:rgba(20,0,0,.62);display:none;align-items:center;justify-content:center;flex-direction:column;}
  #death.on{display:flex;animation:screenfade 1.2s ease-out;}
  #death .big{font-size:32px;letter-spacing:6px;}
  #death .sub{font-size:15px;letter-spacing:2px;margin-top:14px;opacity:.85;text-align:center;max-width:86vw;line-height:1.7;}
  #death .restart{margin-top:26px;pointer-events:auto;cursor:pointer;background:#eaf2f8;color:#111;border:none;border-radius:8px;padding:10px 24px;font-size:15px;font-weight:700;letter-spacing:2px;}
  #death .restart:hover{background:#fff;}
  #title{position:absolute;inset:0;display:none;align-items:center;justify-content:center;flex-direction:column;pointer-events:auto;background:linear-gradient(rgba(4,12,24,.25), rgba(4,12,24,.55));}
  #title.on{display:flex;}
  #title .logo{font-family:Montserrat,system-ui,sans-serif;font-weight:300;font-size:56px;letter-spacing:14px;text-shadow:0 4px 24px rgba(0,0,0,.7);animation:titlein 1.6s ease-out;}
  #title .tsub{font-size:13px;letter-spacing:6px;opacity:.75;margin-top:10px;animation:titlein 2.2s ease-out;}
  #title .tbtns{margin-top:48px;display:flex;gap:18px;flex-wrap:wrap;justify-content:center;animation:titlein 2.6s ease-out;}
  #title .tbtn{cursor:pointer;border:none;border-radius:10px;padding:14px 36px;font-size:16px;font-weight:700;letter-spacing:3px;transition:transform .12s, filter .12s;}
  #title .tbtn:hover{transform:translateY(-2px);filter:brightness(1.15);}
  #title .tbtn.primary{background:#eaf2f8;color:#0a1a28;}
  #title .tbtn.secondary{background:rgba(255,255,255,.14);color:#eaf2f8;box-shadow:0 0 0 1px rgba(255,255,255,.35) inset;}
  #title .tfoot{position:absolute;bottom:22px;font-size:11px;letter-spacing:2px;opacity:.55;}
  @keyframes screenfade{from{opacity:0;}to{opacity:1;}}
  @keyframes camshake{0%,100%{transform:none;}20%{transform:translate(-6px,3px);}40%{transform:translate(5px,-4px);}60%{transform:translate(-4px,-2px);}80%{transform:translate(3px,2px);}}
  @keyframes titlein{0%{opacity:0;transform:translateY(14px);}40%{opacity:0;}100%{opacity:1;transform:none;}}
  body.title-open #touch-actions,body.title-open #gfx-btn,body.title-open #gfx-panel,body.title-open #dbg-toggle,body.title-open #dbg-panel{display:none !important;}
  body.end-open #touch-actions,body.end-open #o2-bar,body.end-open #gfx-btn,body.end-open #gfx-panel,body.end-open #dbg-toggle,body.end-open #dbg-panel{display:none !important;}
  #hud-root.end #goal,#hud-root.end #compass,#hud-root.end #prompt,#hud-root.end #toast,#hud-root.end #hint,#hud-root.end #mode,#hud-root.end #hotbar,#hud-root.end #stats,#hud-root.end #clock,#hud-root.end #crosshair,#hud-root.end #feat-banner,#hud-root.end #place-name,#hud-root.end #feats{display:none !important;}
  #hud-root.title #toast,#hud-root.title #stats,#hud-root.title #hotbar,#hud-root.title #goal,#hud-root.title #compass,#hud-root.title #clock,#hud-root.title #mode,#hud-root.title #hint,#hud-root.title #crosshair,#hud-root.title #prompt,#hud-root.title #feat-banner,#hud-root.title #place-name,#hud-root.title #feats{display:none !important;}
  /* ---- モバイル(縦・小画面)の最適化 ---- */
  #clock{position:absolute;left:50%;top:12px;transform:translateX(-50%);font-size:12px;letter-spacing:1px;background:rgba(0,0,0,.4);padding:4px 12px;border-radius:6px;opacity:.9;}
  #compass{position:absolute;left:50%;top:38px;transform:translateX(-50%);width:260px;height:30px;background:rgba(0,0,0,.35);border-radius:15px;overflow:hidden;}
  #compass .c-center{position:absolute;left:50%;top:0;width:1px;height:100%;background:rgba(255,255,255,.55);}
  #compass .c-mark{position:absolute;top:1px;font-size:14px;transform:translateX(-50%);transition:left .12s linear;text-shadow:0 1px 2px #000;}
  #compass .c-mark em{display:block;font-style:normal;font-size:7px;line-height:1;opacity:.8;text-align:center;white-space:nowrap;}
  #tint{position:absolute;inset:0;pointer-events:none;background:rgba(0,0,0,0);transition:background 2s linear;}
  #dmg{position:absolute;inset:0;pointer-events:none;box-shadow:inset 0 0 120px 40px rgba(200,20,10,.0);transition:box-shadow .5s ease-out;}
  #dmg.on{box-shadow:inset 0 0 140px 60px rgba(200,20,10,.75);transition:box-shadow .06s;}
  #danger{position:absolute;inset:0;pointer-events:none;box-shadow:inset 0 0 160px 54px rgba(185,10,8,.55);opacity:0;transition:opacity .9s ease;}
  #danger.on{animation:dangerpulse 2.2s ease-in-out infinite;}
  @keyframes dangerpulse{0%,100%{opacity:.5;}12%{opacity:1;}34%{opacity:.5;}}
  .bar.warn>i{animation:barwarn 1s ease-in-out infinite;}
  @keyframes barwarn{0%,100%{filter:brightness(1);}50%{filter:brightness(1.9);}}
  #fade{position:absolute;inset:0;pointer-events:none;background:#000;opacity:0;transition:opacity .7s ease;}
  #fade.on{opacity:1;}
  #wet{position:absolute;inset:0;pointer-events:none;opacity:0;transition:opacity 1.6s ease-out;
    background:
      radial-gradient(ellipse 46% 30% at 22% 8%, rgba(255,255,255,.16), transparent 60%),
      radial-gradient(ellipse 30% 22% at 74% 4%, rgba(255,255,255,.13), transparent 60%),
      linear-gradient(rgba(210,235,245,.10), transparent 45%);}
  #wet.on{opacity:1;transition:opacity .05s;}
  #rain{position:absolute;inset:0;width:100%;height:100%;display:none;pointer-events:none;opacity:0;transition:opacity .5s ease;}
  #rain.on{display:block;opacity:1;}
  #toast{position:absolute;left:50%;top:74px;transform:translateX(-50%);display:flex;flex-direction:column;gap:4px;align-items:center;} /* コンパス帯(38+30px)の直下。46だと帯に被っていた */
  #toast .t{background:rgba(0,0,0,.6);padding:5px 12px;border-radius:6px;font-size:12px;opacity:0;animation:toastfade 2.4s ease forwards;}
  @keyframes toastfade{0%{opacity:0;transform:translateY(-6px);}12%{opacity:1;transform:none;}82%{opacity:1;}100%{opacity:0;}}
  /* ---- 祝福演出: レアトースト / 実績バナー / 島名表示 / 実績パネル ---- */
  #toast .t.rare{background:linear-gradient(160deg, rgba(64,48,10,.88), rgba(34,22,4,.92));box-shadow:0 0 0 1px rgba(255,205,110,.65) inset, 0 0 18px rgba(255,190,70,.3);color:#ffedc4;font-weight:600;animation-duration:3.4s;}
  #feat-banner{position:absolute;left:50%;top:20%;transform:translateX(-50%);z-index:60;pointer-events:none;display:none;align-items:center;gap:12px;padding:11px 20px;border-radius:12px;background:linear-gradient(160deg, rgba(52,38,8,.93), rgba(26,16,2,.95));box-shadow:0 0 0 1px rgba(255,205,110,.6) inset, 0 4px 26px rgba(0,0,0,.55), 0 0 34px rgba(255,190,70,.28);overflow:hidden;}
  #feat-banner.on{display:flex;animation:featin 4.3s ease forwards;}
  #feat-banner::after{content:'';position:absolute;inset:0;background:linear-gradient(115deg, transparent 32%, rgba(255,235,180,.34) 46%, transparent 60%);background-size:230% 100%;background-position:120% 0;animation:featshine 1.5s ease .25s 2;} /* 光の帯が2回走る */
  #feat-banner .fic{font-size:30px;filter:drop-shadow(0 2px 4px rgba(0,0,0,.5));}
  #feat-banner .flabel{font-size:9px;letter-spacing:3px;color:#ffd76e;opacity:.9;}
  #feat-banner .fname{font-size:16px;font-weight:700;letter-spacing:1px;color:#fff3da;text-shadow:0 1px 3px #000;margin:1px 0;}
  #feat-banner .fdesc{font-size:10px;opacity:.75;}
  @keyframes featin{0%{opacity:0;transform:translateX(-50%) translateY(-16px) scale(.9);}7%{opacity:1;transform:translateX(-50%) translateY(0) scale(1.02);}11%{transform:translateX(-50%);}88%{opacity:1;transform:translateX(-50%);}100%{opacity:0;transform:translateX(-50%) translateY(-10px);}}
  @keyframes featshine{to{background-position:-130% 0;}}
  #place-name{position:absolute;left:0;right:0;top:34%;text-align:center;pointer-events:none;opacity:0;}
  #place-name.on{animation:placein 3.8s ease forwards;}
  #place-name .pn-name{font-size:38px;font-weight:300;letter-spacing:12px;text-shadow:0 2px 18px rgba(0,0,0,.75), 0 0 40px rgba(120,200,255,.25);}
  #place-name .pn-sub{font-size:12px;letter-spacing:5px;opacity:.8;margin-top:8px;text-shadow:0 1px 4px #000;}
  @keyframes placein{0%{opacity:0;transform:translateY(12px);}12%{opacity:1;transform:none;}80%{opacity:1;}100%{opacity:0;}}
  #feats{position:absolute;left:calc(16px + env(safe-area-inset-left, 0px));top:50%;transform:translateY(-50%);width:300px;max-height:80vh;overflow:auto;background:rgba(6,16,26,.92);border-radius:12px;padding:14px;box-shadow:0 8px 30px rgba(0,0,0,.5);display:none;pointer-events:auto;}
  #feats.on{display:block;}
  #feats h3{font-size:13px;letter-spacing:2px;text-transform:uppercase;opacity:.85;margin-bottom:10px;display:flex;justify-content:space-between;}
  #feats h3 b{color:#ffd76e;letter-spacing:1px;}
  .feat{display:flex;align-items:center;gap:10px;padding:7px 8px;border-radius:8px;margin-bottom:3px;background:rgba(255,255,255,.04);}
  .feat .ic{font-size:20px;width:24px;text-align:center;}
  .feat .nm{flex:1;}
  .feat .nm b{font-size:12px;}
  .feat .nm small{display:block;font-size:9px;opacity:.65;}
  .feat.done{background:linear-gradient(90deg, rgba(80,60,14,.35), rgba(255,255,255,.04));box-shadow:0 0 0 1px rgba(255,205,110,.25) inset;}
  .feat.done .chk{color:#ffd76e;font-size:13px;}
  .feat.lock{opacity:.45;}
  .feat.lock .ic{filter:grayscale(1);}
    @media (max-width: 500px) {
    #hint{display:none;}                                    /* キーボード操作の説明は不要 */
    #mode{font-size:10px;padding:3px 7px;max-width:40vw;overflow:hidden;white-space:nowrap;}
    #mode .co{display:none;}                                 /* デバッグ座標はスマホでは邪魔なので隠す */
    #goal{top:34px;font-size:10px;max-width:56vw;padding:4px 7px;line-height:1.4;}
    #clock{font-size:10px;padding:3px 8px;}
    #compass{width:180px;height:26px;top:88px;}              /* ガイド文(top34+3行分)の下。64だと2行時点で被った */
    #compass .c-mark{font-size:11px;}
    #compass .c-mark em{font-size:6px;}
    #toast{top:118px;}                                       /* コンパス帯(88+26px)の直下 */
    #toast .t{font-size:10px;padding:4px 8px;max-width:82vw;text-align:center;}
    #craft{left:calc(8px + env(safe-area-inset-left, 0px));right:auto;width:min(250px, 62vw);max-height:64vh;} /* 右のボタン列と重ならないよう左へ。80vhだと中央配置の下端がステータスに被る */
    #stats{width:150px;gap:4px;bottom:calc(70px + env(safe-area-inset-bottom, 0px));} /* ホットバーの上へ退避(重なり解消) */
    .bar{height:10px;}
    .bar>span{font-size:8px;line-height:10px;}
    #title .logo{font-size:min(56px, 8.6vw);letter-spacing:min(14px, 1.5vw);max-width:96vw;text-align:center;}
    #title .tsub{font-size:11px;letter-spacing:3px;}
    #title .tfoot{font-size:9px;padding:0 12px;text-align:center;}
    #prompt{font-size:12px;top:56%;}
    #feat-banner{top:24%;max-width:86vw;padding:9px 14px;gap:9px;}
    #feat-banner .fname{font-size:13px;}
    #feat-banner .fdesc{font-size:9px;}
    #place-name .pn-name{font-size:24px;letter-spacing:7px;}
    #place-name .pn-sub{font-size:10px;letter-spacing:3px;}
    #feats{left:calc(8px + env(safe-area-inset-left, 0px));width:min(260px, 66vw);max-height:64vh;}
    /* 持ち物が増えても縦に伸びない: 横1行スクロール(高さ固定)。体力バー/右下ボタンと重ならない。 */
    #hotbar{max-width:56vw;bottom:calc(10px + env(safe-area-inset-bottom, 0px));left:calc(8px + env(safe-area-inset-left, 0px));transform:none;justify-content:flex-start;flex-wrap:nowrap;overflow-x:auto;overflow-y:hidden;scrollbar-width:none;padding-bottom:2px;}
    #hotbar::-webkit-scrollbar{display:none;}
    .slot{width:40px;height:40px;font-size:16px;flex:0 0 auto;}
  }

  `;
  document.head.appendChild(style);

  const root = document.createElement('div');
  root.id = 'hud-root';
  // タッチ端末フラグ: 幅ベースの@mediaだけだと横持ち(844px等)がデスクトップ扱いになり、
  // キーボード操作ヒントが出てしまう。main.tsのIS_TOUCHと同一基準。
  if (('ontouchstart' in window) || navigator.maxTouchPoints > 0) root.classList.add('touch');
  root.innerHTML = `
    <div id="tint"></div>
    <div id="wet"></div>
    <div id="danger"></div>
    <canvas id="rain"></canvas>
    <div id="dmg"></div>
    <div id="fade"></div>
    <div id="crosshair"></div>
    <div id="markers"></div>
    <div id="mode">—</div>
    <div id="goal"></div>
    <div id="clock">—</div>
    <div id="compass"><div class="c-center"></div></div>
    <div id="toast"></div>
    <div id="hint">WASD 移動 / マウス 視点 / Shift 加速<br>Space 浮上・ジャンプ / Ctrl 潜る<br><b>1-9/ホットバー 装備</b> / <b>クリック 使う</b><br>E 調べる(採取・乗る・開ける…)<br>C クラフト / V 実績 / F 食べる</div>
    <div id="stats">
      <div class="bar" id="b-health"><i></i><span>❤️ 体力</span></div>
      <div class="bar" id="b-hunger"><i></i><span>🍖 空腹</span></div>
      <div class="bar" id="b-thirst"><i></i><span>💧 水分</span></div>
      <div class="bar" id="b-stamina"><i></i><span>⚡ スタミナ</span></div>
    </div>
    <div id="hotbar"></div>
    <div id="prompt"></div>
    <div id="craft"><h3>クラフト</h3><div id="craft-list"></div></div>
    <div id="feats"><h3>🏆 実績 <b id="feats-count">0/0</b></h3><div id="feats-list"></div></div>
    <div id="feat-banner"></div>
    <div id="place-name"></div>
    <div id="title">
      <div class="logo">STRANDED ISLAND</div>
      <div class="tsub">— 無人島から生きて帰れ —</div>
      <div class="tbtns"></div>
      <div class="tfoot">島々を渡り、素材を集め、難破船を修理して脱出せよ</div>
    </div>
    <div id="death"><div class="big">力尽きた…</div><div class="sub"></div><button class="restart">もう一度</button></div>
    <div id="win"><div class="big">🎉 脱出成功！</div><div class="rank"></div><div class="sub"></div><button class="restart">もう一度遊ぶ</button></div>
  `;
  document.body.appendChild(root);

  el = {
    markers: root.querySelector('#markers'),
    mode: root.querySelector('#mode'),
    goal: root.querySelector('#goal'),
    win: root.querySelector('#win'),
    winSub: root.querySelector('#win .sub'),
    winRank: root.querySelector('#win .rank'),
    winRestart: root.querySelector('#win .restart'),
    health: root.querySelector('#b-health>i'),
    hunger: root.querySelector('#b-hunger>i'),
    thirst: root.querySelector('#b-thirst>i'),
    stamina: root.querySelector('#b-stamina>i'),
    hotbar: root.querySelector('#hotbar'),
    prompt: root.querySelector('#prompt'),
    craft: root.querySelector('#craft'),
    craftList: root.querySelector('#craft-list'),
    feats: root.querySelector('#feats'),
    featsList: root.querySelector('#feats-list'),
    featsCount: root.querySelector('#feats-count'),
    featBanner: root.querySelector('#feat-banner'),
    placeName: root.querySelector('#place-name'),
    death: root.querySelector('#death'),
    deathSub: root.querySelector('#death .sub'),
    deathRestart: root.querySelector('#death .restart'),
    clock: root.querySelector('#clock'),
    toast: root.querySelector('#toast'),
    compass: root.querySelector('#compass'),
    _compassEls: new Map<string, HTMLElement>(),
    _markerEls: new Map<number, HTMLElement>(),
  };
  el.deathRestart.addEventListener('click', () => { if (handlers.onRestart) handlers.onRestart(); });

  // クラフト一覧を一度生成（状態は refreshCraft で更新）
  for (const r of RECIPES) {
    const out = ITEMS[r.out.item];
    const div = document.createElement('div');
    div.className = 'recipe';
    div.dataset.id = r.id;
    const costStr = Object.entries(r.cost).map(([k, v]) => `${ITEMS[k].icon}${v}`).join(' ');
    const useStr = out.use ? `<small class="use">${out.use}${r.station ? '（要焚き火）' : ''}</small>` : '';
    div.innerHTML = `<div class="ic">${out.icon}</div><div class="nm"><b>${out.name}</b><small>${costStr}</small>${useStr}</div>`;
    div.addEventListener('click', () => { if (handlers.onCraft) handlers.onCraft(r.id); });
    el.craftList.appendChild(div);
  }
  // 画面外レシピの気付き「▼」: 最下段が見えていない間だけ表示(スクロールで随時更新)
  el.craft.addEventListener('scroll', updCraftMore, { passive: true });

  // 実績一覧を一度生成(達成状態は refreshFeats で更新)
  for (const { def } of featList()) {
    const div = document.createElement('div');
    div.className = 'feat lock';
    div.dataset.id = def.id;
    div.innerHTML = `<div class="ic">${def.icon}</div><div class="nm"><b>${def.name}</b><small></small></div><div class="chk"></div>`;
    el.featsList.appendChild(div);
  }
  refreshFeats();
  return el;
}

function updCraftMore(): void {
  el.craft.classList.toggle('more', el.craft.scrollTop + el.craft.clientHeight < el.craft.scrollHeight - 6);
}

export function updateStats(stats: Stats): void {
  el.health.style.width = (stats.health / stats.maxHealth * 100) + '%';
  el.hunger.style.width = (stats.hunger / stats.maxHunger * 100) + '%';
  el.thirst.style.width = (stats.thirst / stats.maxThirst * 100) + '%';
  el.stamina.style.width = (stats.stamina / stats.maxStamina * 100) + '%';
}

// ホットバー: タップ/クリックで装備を切替(装備品が「使う」の意味を決める)。
let _onEquip: ((item: string) => void) | null = null;
let _equippedItem: string | null = null;
export function setHotbarHandlers(onEquip: (item: string) => void): void { _onEquip = onEquip; }
export function setEquipped(item: string | null): void { _equippedItem = item; }
export function updateHotbar(): void {
  const keys = Object.keys(inventory).filter((k) => inventory[k] > 0);
  el.hotbar.innerHTML = keys.map((k, i) => {
    const it = ITEMS[k] || { icon: '?', name: k };
    const num = i < 9 ? `<em>${i + 1}</em>` : '';
    return `<div class="slot${k === _equippedItem ? ' eq' : ''}" data-item="${k}"><small>${it.name}</small>${it.icon}<b>${inventory[k]}</b>${num}</div>`;
  }).join('') || `<div class="slot"><small>空</small></div>`;
  for (const div of el.hotbar.children) {
    const item = (div as HTMLElement).dataset.item;
    if (item) div.addEventListener('pointerdown', (ev: Event) => { ev.stopPropagation(); if (_onEquip) _onEquip(item); });
  }
}

const _isTouch = ('ontouchstart' in window) || navigator.maxTouchPoints > 0;
export function setPrompt(text: string): void {
  if (text) {
    // タッチ端末ではキー表記をボタン名に読み替える
    if (_isTouch) text = text.replace('<kbd>E</kbd>', '<kbd>調べる</kbd>').replace('<kbd>R</kbd>', '<kbd>使う</kbd>');
    el.prompt.innerHTML = text;
    el.prompt.classList.add('on');
  } else el.prompt.classList.remove('on');
}

export function setMode(text: string): void { el.mode.innerHTML = text; }

export function toggleCraft(force?: boolean): boolean {
  const on = force != null ? force : !el.craft.classList.contains('on');
  el.craft.classList.toggle('on', on);
  const hint = document.getElementById('hint'); if (hint) hint.style.visibility = on ? 'hidden' : ''; // 右上の操作説明とパネルの重なりを避ける
  if (on) { toggleFeats(false); refreshCraft(); updCraftMore(); } // 実績パネルとは相互排他
  return on;
}
export function craftOpen(): boolean { return el.craft.classList.contains('on'); }

// --- 実績(マイルストーン)パネル・達成バナー・島名表示 -------------------------------
export function toggleFeats(force?: boolean): boolean {
  const on = force != null ? force : !el.feats.classList.contains('on');
  el.feats.classList.toggle('on', on);
  if (on) { toggleCraft(false); refreshFeats(); } // クラフトパネルとは相互排他
  return on;
}
export function featsOpen(): boolean { return el.feats.classList.contains('on'); }
export function uiOpen(): boolean { return craftOpen() || featsOpen(); } // 何かのパネルを開いている(移動/攻撃/ロックの抑止用)

export function refreshFeats(): void {
  if (!el.featsList) return;
  const list = featList();
  let n = 0;
  for (const div of el.featsList.children) {
    const e = list.find((x) => x.def.id === (div as HTMLElement).dataset.id);
    if (!e) continue;
    if (e.unlocked) n++;
    div.className = 'feat ' + (e.unlocked ? 'done' : 'lock');
    const sm = div.querySelector('small'); if (sm) sm.textContent = e.unlocked ? e.def.desc : e.def.hint;
    const chk = div.querySelector('.chk'); if (chk) chk.textContent = e.unlocked ? '✓' : '';
  }
  el.featsCount.textContent = `${n}/${list.length}`;
}

// 実績達成バナー(金色)。連続達成はキューで順番に見せる(同時に重ねない)。
const featQ: { icon: string; name: string; desc: string; n: number; total: number }[] = [];
let featBusy = false;
export function showFeat(def: { icon: string; name: string; desc: string }, n: number, total: number): void {
  featQ.push({ icon: def.icon, name: def.name, desc: def.desc, n, total });
  pumpFeat();
}
function pumpFeat(): void {
  if (featBusy || !featQ.length) return;
  const it = featQ.shift()!;
  featBusy = true;
  const b = el.featBanner as HTMLElement;
  b.innerHTML = `<div class="fic">${it.icon}</div><div class="fbody"><div class="flabel">🏆 実績達成 ${it.n}/${it.total}</div><div class="fname">${it.name}</div><div class="fdesc">${it.desc}</div></div>`;
  b.classList.remove('on'); void b.offsetWidth; // reflowでアニメ再発火
  b.classList.add('on');
  refreshFeats(); // パネルを開いていても即時反映
  setTimeout(() => { b.classList.remove('on'); featBusy = false; pumpFeat(); }, 4400);
}

// 島名の大表示(初上陸の映画的タイトル)。実績バナー(20%)より下の34%に出る。
export function showPlaceName(name: string, sub: string): void {
  const p = el.placeName as HTMLElement;
  p.innerHTML = `<div class="pn-name">${name}</div><div class="pn-sub">${sub}</div>`;
  p.classList.remove('on'); void p.offsetWidth;
  p.classList.add('on');
}

export function refreshCraft(): void {
  for (const div of el.craftList.children) {
    const r = RECIPES.find((x) => x.id === div.dataset.id);
    if (!r) continue;
    div.classList.toggle('no', !canCraft(r));
    // 素材ごとに 所持/必要 を色付きで表示(足りない素材がひと目で分かる)
    const sm = div.querySelector('small');
    if (sm) sm.innerHTML = Object.entries(r.cost).map(([k, v]) => {
      const have = count(k);
      return `<span style="color:${have >= (v as number) ? '#9fe3a1' : '#f29a92'}">${ITEMS[k].icon}${have}/${v}</span>`;
    }).join(' ');
  }
}

export function showDeath(on: boolean, daysSurvived?: number, statsText?: string): void {
  el.death.classList.toggle('on', !!on);
  document.body.classList.toggle('end-open', !!on);
  const root = document.getElementById('hud-root'); if (root) root.classList.toggle('end', !!on);
  if (on) el.deathSub.innerHTML = `生存 ${daysSurvived ?? 0} 日` + (statsText ? `<br><small style="opacity:.75;letter-spacing:1px">${statsText}</small>` : '');
}

// 脱出成功(クリア)画面(プレイ統計付き)
export function showVictory(day: number, statsText: string, onRestart: () => void, rank = ''): void {
  document.body.classList.add('end-open');
  const root = document.getElementById('hud-root'); if (root) root.classList.add('end');
  el.winRank.textContent = rank; // 例: 🏆S — 脱出日数の評価を大きく見せる
  el.winSub.innerHTML = `${day} 日目、修理した船で島々を後にした<br><small style="opacity:.75;letter-spacing:1px">${statsText}</small>`;
  // 紙吹雪(CSSアニメ): 脱出成功の祝祭感
  if (!el.win.querySelector('.cf')) {
    const colors = ['#ffd76e', '#ff8e8e', '#8fd0ff', '#b6f0a6', '#f0a6e8'];
    for (let i = 0; i < 36; i++) {
      const c = document.createElement('i'); c.className = 'cf';
      c.style.left = `${Math.random() * 100}%`;
      c.style.background = colors[i % colors.length];
      c.style.animationDuration = `${3 + Math.random() * 3}s`;
      c.style.animationDelay = `${Math.random() * 4}s`;
      el.win.appendChild(c);
    }
  }
  el.win.classList.add('on');
  el.winRestart.onclick = onRestart;
}

// 雨エフェクト(Canvasパーティクル): 3層パララックス+風の揺らぎ+着水しぶき。
// 旧実装(全画面DOMのbackground-positionアニメ)は合成レイヤに乗らずCPU再描画が毎フレーム
// 走って重く、均一ストライプで安っぽかった。半解像度Canvasに線分を描く方式は
// 描画コストが1ms未満で、雨脚の濃淡・遠近・風向きが表現できる。
const RAIN_RES = 0.5; // 内部解像度(CSSピクセル比)。半分で十分シャープ
interface Drop { x: number; y: number; len: number; spd: number; a: number; w: number }
interface Splash { x: number; y: number; r: number; life: number }
let rainOn = false, rainRaf = 0, rainHideTimer = 0;
let drops: Drop[] = [];
const splashes: Splash[] = [];
let rainLast = 0;

function rainCanvas(): HTMLCanvasElement | null { return document.getElementById('rain') as HTMLCanvasElement | null; }

function initDrops(w: number, h: number): void {
  const isTouch = ('ontouchstart' in window) || navigator.maxTouchPoints > 0;
  // 3層: [本数, 長さ, 速度(px/s), 不透明度, 太さ] 奥=細く遅く薄い / 手前=太く速く濃い
  const layers: [number, number, number, number, number][] = isTouch
    ? [[26, 10, 620, 0.20, 1], [30, 16, 900, 0.32, 1], [20, 26, 1300, 0.45, 1.6]]
    : [[55, 11, 640, 0.20, 1], [60, 18, 930, 0.33, 1.3], [42, 28, 1350, 0.48, 1.8]];
  drops = [];
  for (const [n, len, spd, a, wd] of layers) {
    for (let i = 0; i < n; i++) {
      drops.push({
        x: Math.random() * w, y: Math.random() * h,
        len: len * (0.8 + Math.random() * 0.5),
        spd: spd * (0.85 + Math.random() * 0.3),
        a: a * (0.7 + Math.random() * 0.6), w: wd,
      });
    }
  }
  splashes.length = 0;
}

function rainFrame(now: number): void {
  const c = rainCanvas();
  if (!c) { rainRaf = 0; return; }
  const ctx = c.getContext('2d');
  if (!ctx) { rainRaf = 0; return; }
  const dt = Math.min((now - rainLast) / 1000, 0.05); rainLast = now;
  const w = c.width, h = c.height;
  ctx.clearRect(0, 0, w, h);
  // 風: ゆっくりした基調 + 突風の揺らぎ(全滴共通。雨脚が波打つ)
  const t = now * 0.001;
  const wind = 0.22 + 0.10 * Math.sin(t * 0.7) + 0.05 * Math.sin(t * 2.3);
  ctx.lineCap = 'round';
  for (const d of drops) {
    const vx = d.spd * wind, vy = d.spd;
    d.x += vx * dt; d.y += vy * dt;
    if (d.y - d.len > h) { // 画面下へ抜けたら上へ再投入(手前層はたまに着水しぶき)
      if (d.w > 1.4 && splashes.length < 24 && Math.random() < 0.5) splashes.push({ x: d.x, y: h - Math.random() * h * 0.06, r: 1, life: 0.24 });
      d.y = -d.len - Math.random() * h * 0.2; d.x = Math.random() * (w + w * 0.3) - w * 0.2;
    }
    ctx.strokeStyle = `rgba(205,226,246,${d.a})`;
    ctx.lineWidth = d.w;
    ctx.beginPath();
    ctx.moveTo(d.x, d.y);
    ctx.lineTo(d.x - vx * (d.len / vy), d.y - d.len);
    ctx.stroke();
  }
  // 着水しぶき(広がって消える小さな弧)
  for (let i = splashes.length - 1; i >= 0; i--) {
    const s = splashes[i];
    s.life -= dt; s.r += dt * 26;
    if (s.life <= 0) { splashes.splice(i, 1); continue; }
    ctx.strokeStyle = `rgba(215,235,250,${s.life * 1.4})`;
    ctx.lineWidth = 1;
    ctx.beginPath();
    ctx.ellipse(s.x, s.y, s.r, s.r * 0.36, 0, Math.PI, Math.PI * 2);
    ctx.stroke();
  }
  rainRaf = requestAnimationFrame(rainFrame); // OFF後もフェード完了までは動かし続ける(タイマーが止める)
}

export function setRain(on: boolean): void {
  if (on === rainOn) return; // 毎フレーム呼ばれるため冪等に
  rainOn = on;
  const c = rainCanvas();
  if (!c) return;
  if (on) {
    clearTimeout(rainHideTimer);
    const w = Math.floor(innerWidth * RAIN_RES), h = Math.floor(innerHeight * RAIN_RES);
    if (c.width !== w || c.height !== h) { c.width = w; c.height = h; }
    initDrops(w, h);
    c.classList.add('on');
    rainLast = performance.now();
    if (!rainRaf) rainRaf = requestAnimationFrame(rainFrame);
  } else {
    c.classList.remove('on'); // CSSの opacity .5s でフェードアウト
    clearTimeout(rainHideTimer);
    rainHideTimer = window.setTimeout(() => { if (rainRaf) { cancelAnimationFrame(rainRaf); rainRaf = 0; } }, 550);
  }
}

// 夜間の自動露出補正(canvasのCSS filter)。人間の暗順応の表現=夜/嵐でも見える。
let _lastExposure = 0;
export function setExposure(v: number): void {
  const c = document.querySelector('canvas') as HTMLElement | null;
  if (!c) return;
  const r = Math.round(v * 100) / 100;
  if (r === _lastExposure) return;
  _lastExposure = r;
  if (!c.style.transition || !c.style.transition.includes('filter')) c.style.transition = 'filter 2s ease';
  c.style.filter = r > 1.01 ? `brightness(${r}) contrast(0.97)` : '';
}

// 昼夜の色調(半透明オーバーレイ)。tod=0..1(0=深夜,0.25=朝6時,0.5=正午,0.75=18時)
// mix-blend-modeはWebGPU canvasに効かないため、rgbaの重ねで暗さ/色味を出す。
let _lastTintKey = '';
export function setDayTint(tod: number): void {
  const el2 = document.getElementById('tint');
  if (!el2) return;
  const lerp = (a: number[], b: number[], t: number) => a.map((v, i) => v + (b[i] - v) * t);
  // 夜は「月明かりで見える暗さ」に留める(濃すぎると何も見えない)
  // 朝はピンク寄り・夕は濃いオレンジで表情を分ける
  const NIGHT = [18, 30, 78, 0.30], DAWN = [255, 140, 110, 0.12], DUSK = [220, 90, 20, 0.16], DAY = [0, 0, 0, 0];
  let c = DAY, warm = 0; // warm=朝夕の暖色の強さ(0..1)。水平線側を濃くするグラデーションに使う
  if (tod < 0.17 || tod >= 0.88) c = NIGHT;
  else if (tod < 0.30) { const t = (tod - 0.17) / 0.13; c = t < 0.5 ? lerp(NIGHT, DAWN, t * 2) : lerp(DAWN, DAY, (t - 0.5) * 2); warm = 1 - Math.abs(t - 0.5) * 2; }
  else if (tod >= 0.70) { const t = (tod - 0.70) / 0.18; c = t < 0.5 ? lerp(DAY, DUSK, t * 2) : lerp(DUSK, NIGHT, (t - 0.5) * 2); warm = 1 - Math.abs(t - 0.5) * 2; }
  const rgba = (a: number[], mul = 1) => `rgba(${Math.round(a[0])}, ${Math.round(a[1])}, ${Math.round(a[2])}, ${+(a[3] * mul).toFixed(2)})`;
  // 朝夕は空側が薄く水平線(画面中央〜下)側が濃い夕焼けグラデーション。夜/昼は均一
  const key = warm > 0.05
    ? `linear-gradient(${rgba(c, 0.5)} 0%, ${rgba(c, 1.4)} 55%, ${rgba(c, 0.9)} 100%)`
    : rgba(c);
  if (key !== _lastTintKey) { _lastTintKey = key; el2.style.background = key; }
}

// 暗転→処理→明け(休息などの場面転換)
export function fadeTransition(mid: () => void, holdMs = 700): void {
  const f = document.getElementById('fade');
  if (!f) { mid(); return; }
  f.classList.add('on');
  setTimeout(() => { mid(); setTimeout(() => f.classList.remove('on'), holdMs); }, 750);
}

// 浮上時の「顔から水が滴る」オーバーレイ(一瞬出てゆっくり消える)
export function splashWet(): void {
  const w = document.getElementById('wet');
  if (!w) return;
  w.classList.add('on');
  setTimeout(() => w.classList.remove('on'), 120);
}

// 体力危機(HP<25)の赤ビネット。心音sfx(2.2秒周期)とCSSアニメの周期を合わせて脈動させる
let _dangerOn = false;
export function setDanger(on: boolean): void {
  if (on === _dangerOn) return; // 毎フレーム呼ばれるため冪等に
  _dangerOn = on;
  const d = document.getElementById('danger');
  if (d) d.classList.toggle('on', on);
}

// 被ダメージの赤フラッシュ + 画面シェイク(canvasをCSSで数px揺らす。3Dカメラには触れない)
export function flashDamage(): void {
  const d = document.getElementById('dmg');
  if (!d) return;
  d.classList.add('on');
  setTimeout(() => d.classList.remove('on'), 80);
  const cv = document.querySelector('canvas');
  if (cv) {
    cv.style.animation = 'none';
    void cv.offsetWidth; // reflowでアニメ再発火
    cv.style.animation = 'camshake .28s ease-out';
  }
}

// クラフト成功時に該当スロットを一瞬光らせる(手応え)
export function flashSlot(item: string): void {
  const slot = el.hotbar?.querySelector(`[data-item="${item}"]`);
  if (!slot) return;
  slot.classList.remove('flash');
  void (slot as HTMLElement).offsetWidth; // reflowでアニメ再発火
  slot.classList.add('flash');
}

// 空腹/渇きが危険域・スタミナ枯渇のときバーを点滅
export function setBarWarnings(hungerLow: boolean, thirstLow: boolean, gassed = false): void {
  const h = document.getElementById('b-hunger'), t = document.getElementById('b-thirst'), s = document.getElementById('b-stamina');
  if (h) h.classList.toggle('warn', hungerLow);
  if (t) t.classList.toggle('warn', thirstLow);
  if (s) s.classList.toggle('warn', gassed);
}

// 目標(クエスト)表示。空文字で非表示。
export function setGoal(text: string): void {
  el.goal.textContent = text;
  el.goal.style.display = text ? 'block' : 'none';
}

// タイトル画面(常時スタート地点)。背景はシネマティックカメラが島を周回する。
// savedDay があれば「続きから」も出す。選択でコールバック→ゲーム開始。
export function showTitle(savedDay: number | null, handlers: { onStart: () => void; onContinue?: () => void }): void {
  const root = document.getElementById('hud-root');
  const t = document.getElementById('title');
  if (!root || !t) return;
  root.classList.add('title');
  document.body.classList.add('title-open');
  t.classList.add('on');
  const btns = t.querySelector('.tbtns') as HTMLElement;
  btns.innerHTML = '';
  const close = () => { t.classList.remove('on'); root.classList.remove('title'); document.body.classList.remove('title-open'); };
  if (savedDay != null && handlers.onContinue) {
    const c = document.createElement('button');
    c.className = 'tbtn primary'; c.textContent = `続きから (Day ${savedDay})`;
    c.addEventListener('click', () => { close(); handlers.onContinue!(); });
    btns.appendChild(c);
  }
  const s = document.createElement('button');
  s.className = 'tbtn ' + (savedDay != null ? 'secondary' : 'primary');
  s.textContent = savedDay != null ? 'はじめから' : '漂流をはじめる';
  s.addEventListener('click', () => { close(); handlers.onStart(); });
  btns.appendChild(s);
}

// (旧 showStartMenu は削除: シネマティックタイトル showTitle が「続きから/はじめから」を担う)

export function updateClock(text: string): void { el.clock.textContent = text; }

// コンパス帯: 目標(船/島)の相対方位を表示。rel=0で中央(正面)、±maxRelで端にクランプ。
export function updateCompass(marks: { key: string; icon: string; label: string; rel: number; dist?: number }[]): void {
  const W = el.compass.clientWidth || 260, MAXREL = Math.PI * 0.75; // 表示レンジ±135°。幅は実測(モバイルはCSSで180px。260固定だと端のマークが帯の外=切れて見えなかった)
  const seen = new Set<string>();
  const placed: { d: HTMLElement; em: HTMLElement | null; x: number; cen: number }[] = [];
  for (const m of marks) {
    seen.add(m.key);
    let d = el._compassEls.get(m.key);
    if (!d) {
      d = document.createElement('div');
      d.className = 'c-mark';
      el.compass.appendChild(d);
      el._compassEls.set(m.key, d);
      d.innerHTML = `${m.icon}<em>${m.label}</em>`;
    }
    const em = d.querySelector('em') as HTMLElement | null;
    if (em) { const txt = m.dist != null ? `${m.label} ${m.dist}m` : m.label; if (em.textContent !== txt) { em.textContent = txt; (d as any).__w = 0; } } // 距離は毎フレーム変わるため更新(幅キャッシュも無効化)
    const rel = Math.max(-MAXREL, Math.min(MAXREL, m.rel));
    const x = W / 2 + (rel / MAXREL) * (W / 2 - 12);
    d.style.left = x + 'px';
    d.style.opacity = Math.abs(m.rel) > MAXREL ? '0.45' : '1'; // レンジ外は薄く(端に張り付き)
    placed.push({ d, em, x, cen: Math.abs(m.rel) });
  }
  // ラベルの重なり解消: マークが密集すると文字が団子になり判読不能だった。
  // 正面(rel小)に近いものからラベルを残し、既存ラベルと重なる分は文字だけ隠す(アイコンは残る)。
  placed.sort((a, b) => a.cen - b.cen);
  const kept: { l: number; r: number }[] = [];
  for (const p of placed) {
    if (!p.em) continue;
    let w = (p.d as any).__w;
    if (!w) w = (p.d as any).__w = p.em.offsetWidth || 30; // テキスト変更時のみ実測(毎フレームのreflowを避ける)
    const l = p.x - w / 2, r = p.x + w / 2;
    // 既存ラベルとの重なり、または帯の端で文字が見切れる場合は隠す(端=レンジ外の目標なのでアイコンで足りる)
    const hit = l < 2 || r > W - 2 || kept.some((k) => l < k.r + 3 && r > k.l - 3);
    p.em.style.visibility = hit ? 'hidden' : 'visible';
    if (!hit) kept.push({ l, r });
  }
  for (const [k, d] of el._compassEls) if (!seen.has(k)) { d.remove(); el._compassEls.delete(k); }
}

export function toast(msg: string, kind?: 'rare'): void {
  // 同文の連打(空振り連打等)はスタックさせず、既存トーストの表示時間を延長してアニメを打ち直す
  const life = kind === 'rare' ? 3500 : 2500; // レアは金色でやや長く(見せ場)
  const last = el.toast.lastElementChild as HTMLElement | null;
  if (last && last.textContent === msg) {
    clearTimeout((last as any).__rm);
    (last as any).__rm = setTimeout(() => last.remove(), life);
    last.style.animation = 'none'; void last.offsetWidth; last.style.animation = '';
    return;
  }
  const d = document.createElement('div');
  d.className = kind ? `t ${kind}` : 't';
  d.textContent = msg;
  el.toast.appendChild(d);
  (d as any).__rm = setTimeout(() => d.remove(), life);
  while (el.toast.children.length > 4 && el.toast.firstChild) el.toast.removeChild(el.toast.firstChild);
}

// 資源マーカーは「今照準で狙っている1つ」だけ表示する（実アセットが見えているので
// 全ノードにマーカーを出すと煩い）。target=採取対象ノード or null。
export function updateMarkers(camera: any, vw: number, vh: number, player: any, target: ResourceNode | null): void {
  const seen = new Set<number>();
  if (target && target.amount > 0) {
    const dist = Math.hypot(target.x - player.pos.x, target.z - player.pos.z);
    const pr = projectToScreen(camera, target.x, target.y + 1.0, target.z, vw, vh);
    if (pr.onScreen) {
      seen.add(target.id);
      let m = el._markerEls.get(target.id);
      if (!m) { m = document.createElement('div'); m.className = 'marker'; el.markers.appendChild(m); el._markerEls.set(target.id, m); }
      m.style.left = pr.x + 'px';
      m.style.top = pr.y + 'px';
      m.style.fontSize = '24px';
      m.style.opacity = '1';
      m.innerHTML = `${target.def.icon}<em>${target.def.label} · ${Math.round(dist / WORLD_PER_M)}m</em>`;
    }
  }
  for (const [id, m] of el._markerEls) {
    if (!seen.has(id)) { m.remove(); el._markerEls.delete(id); }
  }
}
